/**
 * The trade card on screen (Wilf's book, docs/TRADE-OFFLINE.md): a slip of paper clipped over the game at a
 * door, a buyer or your stash. A strip at the top says what you can carry, this hour's limit and any tip that
 * applies here; then one row per good — its price, a plain note (what's left, what you paid, the risk) and
 * its buttons (BUY 1 / BUY MAX, SELL 1 / SELL ALL, PUT AWAY / TAKE). ↑ ↓ choose a row, ← → a button, Enter
 * presses it, Esc (or ✕) shuts the card. Touch: tap. It only draws: the rows come from the `TradeCardModel`.
 */
import { el, esc, html, setText, toggle } from '../ui/dom';
import type { NavAction } from '../ui/screens';
import type { ModuleOverlay } from '../ui/uiTypes';
import './trade.css';

export interface TradeCardButton {
  id: string;
  label: string;
  enabled: boolean;
}

export interface TradeCardRow {
  id: string;
  /** A ruled heading above this row ("Buy here", "Sell here"). */
  section?: string;
  label: string;
  /** The price ("£22 a watch"). */
  value: string;
  /** One plain line under it. */
  note: string;
  /** ▲ worth selling / ▼ at a loss / a warning. */
  tone?: 'up' | 'down' | 'warn';
  buttons: TradeCardButton[];
}

export interface TradeCardInfo {
  /** "Pockets 2 of 6 · Boot of the Haulmaster 12 of 40". */
  carry: string;
  /** "This hour: £640 of £2,500". */
  limit: string;
  /** A tip that applies here, or null. */
  tip: string | null;
  /** The last thing that happened ("Bought 3 watches."), red when it was a no. */
  say: string;
  bad: boolean;
}

export interface TradeCardModel {
  title: string;
  sub: string;
  balance(): number;
  info(): TradeCardInfo;
  rows(): TradeCardRow[];
  press(row: string, button: string): boolean;
  close(): void;
  touch(): boolean;
  sound(s: 'tick' | 'click' | 'confirm' | 'back' | 'whoosh' | 'denied'): void;
}

const pounds = (n: number): string => `£${n.toLocaleString('en-GB')}`;

export class TradeCard implements ModuleOverlay {
  readonly root: HTMLElement;
  index = 0;
  button = 0;
  private readonly list: HTMLElement;
  private readonly cash: HTMLElement;
  private readonly strip: HTMLElement;
  private readonly sayEl: HTMLElement;
  private rows: TradeCardRow[] = [];

  constructor(parent: HTMLElement, private readonly model: TradeCardModel) {
    this.root = el('div', 'bh-screen tr-screen', undefined, parent);
    toggle(this.root, 'touch', model.touch());
    this.root.addEventListener('click', (e) => {
      if (e.target === this.root) {
        model.sound('back');
        model.close();
      }
    });
    const card = el('div', 'tr-card bh-paper', undefined, this.root);
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-label', model.title);
    const head = el('div', 'tr-head', undefined, card);
    html('div', 'tr-title', `${esc(model.title)}<span>${esc(model.sub)}</span>`, head);
    this.cash = el('div', 'tr-cash', '', head);
    const close = el('button', 'tr-close bh-x', '✕', head);
    close.type = 'button';
    close.setAttribute('aria-label', 'Done');
    close.addEventListener('click', (e) => {
      e.stopPropagation();
      model.sound('back');
      model.close();
    });
    this.strip = el('div', 'tr-strip', '', card);
    this.list = el('div', 'tr-list', undefined, card);
    this.list.dataset.touchScroll = '';
    this.sayEl = el('div', 'tr-say', '', card);
    if (!model.touch()) html('div', 'tr-foot', '<span>↑ ↓ choose</span><span>← → button</span><span>Enter do it</span><span>Esc done</span>', card);
    this.render();
  }

  render(): void {
    const rows = this.model.rows();
    this.rows = rows;
    this.index = Math.max(0, Math.min(this.index, rows.length - 1));
    const sel = rows[this.index];
    this.button = sel ? Math.max(0, Math.min(this.button, sel.buttons.length - 1)) : 0;
    setText(this.cash, pounds(this.model.balance()));
    const info = this.model.info();
    this.strip.innerHTML = `<span class="tr-carry">${esc(info.carry)}</span><span class="tr-limit">${esc(info.limit)}</span>${info.tip ? `<span class="tr-tip">${esc(info.tip)}</span>` : ''}`;
    setText(this.sayEl, info.say);
    toggle(this.sayEl, 'bad', info.bad);
    this.list.textContent = '';
    let section = '';
    rows.forEach((r, i) => {
      if (r.section && r.section !== section) {
        section = r.section;
        el('div', 'tr-section', r.section, this.list);
      }
      const row = el('div', `tr-row${r.tone ? ` ${r.tone}` : ''}`, undefined, this.list);
      row.dataset.row = r.id;
      toggle(row, 'sel', i === this.index);
      row.addEventListener('click', () => {
        if (this.index !== i) {
          this.index = i;
          this.button = 0;
          this.model.sound('tick');
          this.render();
        }
      });
      const left = el('div', 'tr-what', undefined, row);
      el('div', 'tr-label', r.label, left);
      el('div', 'tr-note', r.note, left);
      el('div', 'tr-value', r.value, row);
      const btns = el('div', 'tr-btns', undefined, row);
      r.buttons.forEach((b, j) => {
        const btn = el('button', 'tr-btn', b.label, btns);
        btn.type = 'button';
        btn.dataset.btn = b.id;
        btn.disabled = !b.enabled;
        toggle(btn, 'focus', i === this.index && j === this.button);
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.index = i;
          this.button = j;
          this.press();
        });
      });
    });
    this.list.querySelector('.tr-row.sel')?.scrollIntoView?.({ block: 'nearest' });
  }

  private press(): void {
    const r = this.rows[this.index];
    const b = r?.buttons[this.button];
    if (!r || !b) return;
    if (!b.enabled) {
      this.model.sound('denied');
      return;
    }
    const ok = this.model.press(r.id, b.id);
    this.model.sound(ok ? 'confirm' : 'denied');
    this.render();
  }

  onNav(a: NavAction): boolean {
    const n = this.rows.length;
    switch (a) {
      case 'up':
      case 'down':
        if (!n) return true;
        this.index = (this.index + (a === 'up' ? -1 : 1) + n) % n;
        this.button = 0;
        this.model.sound('tick');
        this.render();
        return true;
      case 'left':
      case 'right': {
        const m = this.rows[this.index]?.buttons.length ?? 0;
        if (m > 1) {
          this.button = (this.button + (a === 'left' ? -1 : 1) + m) % m;
          this.model.sound('tick');
          this.render();
        }
        return true;
      }
      case 'confirm':
        this.press();
        return true;
      case 'back':
        this.model.sound('back');
        this.model.close();
        return true;
    }
    return false;
  }

  dispose(): void {
    this.root.remove();
  }
}
